<template>
	<div class="operation-profile-page" v-loading="loading">
		<section class="hero-panel">
			<div class="hero-panel__glow hero-panel__glow--left"></div>
			<div class="hero-panel__glow hero-panel__glow--right"></div>

			<div class="page-header">
				<div class="page-header__title">
					<h2 class="page-title">调压器运行特征画像</h2>
					<p class="page-subtitle">数据来源：NQI 调压器运行画像详情接口。</p>
				</div>
				<div class="header-actions">
					<div class="selected-station" @click="openStationDialog">
						<span class="selected-station__label">调压器设备编号</span>
						<strong class="selected-station__value">{{ selectedStation.regulatorCode || '--' }}</strong>
					</div>
					<el-button
						type="primary"
						size="small"
						icon="el-icon-refresh"
						:loading="analyzing"
						@click="handleReAnalyze"
					>
						重新分析
					</el-button>
				</div>
			</div>
		</section>

		<OverviewSection class="section-block" :profile-data="profileData" />
		<DeviationSection class="section-block" :profile-data="profileData" />
		<TrendSection class="section-block" :profile-data="profileData" />
		<ConclusionSection class="section-block" :profile-data="profileData" />

		<StationSelectDialog
			:show.sync="stationDialogVisible"
			:selected-station="selectedStation"
			@select="handleStationSelect"
		/>
	</div>
</template>

<script>
import OverviewSection from './components/OverviewSection.vue'
import DeviationSection from './components/DeviationSection.vue'
import TrendSection from './components/TrendSection.vue'
import ConclusionSection from './components/ConclusionSection.vue'
import StationSelectDialog from './components/StationSelectDialog.vue'
import { apiGetPressureRegulatorList, apiGetPressureRegulatorPortraitDetail } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'OperationProfile',
	components: { OverviewSection, DeviationSection, TrendSection, ConclusionSection, StationSelectDialog },
	data() {
		return {
			loading: false,
			selectedDevice: '',
			selectedStation: {},
			analyzing: false,
			stationDialogVisible: false,
			profileData: this.getEmptyProfileData(),
		}
	},
	created() {
		this.initDefaultStation()
	},
	methods: {
		async initDefaultStation() {
			const routeDeviceNo = this.$route.query.deviceNo || this.$route.query.collectorDeviceNo
			this.loading = true
			try {
				const params = {
					current: 1,
					size: 10,
				}
				if (routeDeviceNo) {
					params.collectorDeviceNo = routeDeviceNo
				}
				const res = await apiGetPressureRegulatorList(params)
				const records = res.records || []
				const station = records.find(item => item.collectorDeviceNo) || {}
				this.selectedStation = station.collectorDeviceNo ? { ...station } : {}
				this.selectedDevice = station.collectorDeviceNo || ''
			} catch (error) {
				this.selectedDevice = ''
				this.selectedStation = {}
			} finally {
				this.loading = false
			}
			this.fetchProfileData()
		},
		openStationDialog() {
			this.stationDialogVisible = true
		},
		handleStationSelect(row) {
			this.selectedStation = { ...row }
			this.selectedDevice = row.collectorDeviceNo
			this.fetchProfileData()
		},
		handleReAnalyze() {
			this.analyzing = true
			this.fetchProfileData().finally(() => {
				this.analyzing = false
				this.$message.success('分析完成')
			})
		},
		async fetchProfileData() {
			if (!this.selectedDevice) {
				this.profileData = this.getEmptyProfileData()
				return
			}
			this.loading = true
			try {
				const data = await apiGetPressureRegulatorPortraitDetail({
					deviceNo: this.selectedDevice,
				})
				this.profileData = this.normalizeProfileData(data || {})
			} catch (error) {
				this.profileData = this.getEmptyProfileData(this.selectedDevice)
			} finally {
				this.loading = false
			}
		},
		normalizeProfileData(raw) {
			const overview = raw.overview || {}
			const conclusion = raw.conclusion || {}
			const metricOverview = raw.metricOverview || {}
			const pressureItems = this.normalizeMetricItems(metricOverview.pressureMetrics, 'pressure')
			const flowItems = this.normalizeMetricItems(metricOverview.flowMetrics, 'flow')
			const temperatureItems = this.normalizeMetricItems(metricOverview.temperatureMetrics, 'temperature')
			const allMetrics = [...pressureItems, ...flowItems, ...temperatureItems]
			const metricMap = allMetrics.reduce((map, item) => {
				map[item.key] = item
				return map
			}, {})

			return {
				deviceNo: raw.deviceNo || overview.deviceNo || this.selectedDevice,
				status: this.normalizeStatus(conclusion.finalStatus || overview.currentStatus),
				analyzeTime: raw.analysisTime || overview.analysisTime || '--',
				statDate: raw.statDate || '--',
				anomalyType: conclusion.portraitType || overview.portraitType || '--',
				clusterDistance: this.formatNumber(conclusion.distScore ?? overview.distScore),
				pressure: this.buildMetricGroup(pressureItems),
				flow: this.buildMetricGroup(flowItems),
				temperature: this.buildMetricGroup(temperatureItems),
				deviations: this.normalizeDeviations(raw.deviationMetrics || [], metricMap),
				trends: this.normalizeTrends(raw.trendEvidence || [], metricMap),
				conclusion: this.normalizeConclusion(conclusion, raw.deviationMetrics || [], metricMap),
			}
		},
		normalizeMetricItems(items = [], type) {
			return items.map(item => {
				const deviation = this.toNumber(item.deviationZ)
				const status = this.isAbnormalMetric(item) ? 'abnormal' : 'normal'
				return {
					key: item.metricCode,
					label: item.metricName || item.metricCode,
					value: this.formatMetricNumber(item.metricValue, item.metricName),
					unit: this.getMetricUnit(item.metricName),
					type,
					deviation,
					direction: status === 'abnormal' ? this.getDirection(item.displayLabel, deviation) : '',
					status,
					historyMean: this.formatMetricNumber(item.historyMean, item.metricName),
					historySigma: this.formatMetricNumber(item.historyStd, item.metricName),
					barLower: this.formatMetricNumber(item.barLower, item.metricName),
					barUpper: this.formatMetricNumber(item.barUpper, item.metricName),
					displayLabel: item.displayLabel,
					statusLabel: item.statusLabel,
					abnormalCategoryName: item.abnormalCategoryName,
					handlingSuggestion: item.handlingSuggestion,
				}
			})
		},
		buildMetricGroup(items) {
			return {
				status: items.some(item => item.status === 'abnormal') ? 'abnormal' : 'normal',
				items,
			}
		},
		normalizeDeviations(items, metricMap) {
			const source = items.length ? items : Object.values(metricMap)
			return source.map(item => {
				const metric = metricMap[item.metricCode || item.key] || {}
				const deviation = this.toNumber(item.deviationZ ?? item.deviation ?? metric.deviation)
				const status = this.isAbnormalMetric(item) || metric.status === 'abnormal' ? 'abnormal' : 'normal'
				const metricName = item.metricName || metric.label
				return {
					key: item.metricCode || metric.key,
					label: metricName,
					value: this.hasValue(item.metricValue)
						? this.formatMetricNumber(item.metricValue, metricName)
						: metric.value,
					unit: metric.unit || this.getMetricUnit(metricName),
					type: metric.type || this.getMetricType(item.metricCode || metric.key),
					deviation,
					historyMean: this.hasValue(item.historyMean)
						? this.formatMetricNumber(item.historyMean, metricName)
						: metric.historyMean,
					historySigma: this.hasValue(item.historyStd)
						? this.formatMetricNumber(item.historyStd, metricName)
						: metric.historySigma,
					barLower: this.hasValue(item.barLower)
						? this.formatMetricNumber(item.barLower, metricName)
						: metric.barLower,
					barUpper: this.hasValue(item.barUpper)
						? this.formatMetricNumber(item.barUpper, metricName)
						: metric.barUpper,
					direction:
						status === 'abnormal'
							? this.getDirection(item.displayLabel || metric.displayLabel, deviation)
							: '',
					status,
					displayLabel: item.displayLabel || metric.displayLabel,
					statusLabel: item.statusLabel || metric.statusLabel,
					abnormalCategoryName: item.abnormalCategoryName || metric.abnormalCategoryName,
					handlingSuggestion: item.handlingSuggestion || metric.handlingSuggestion,
				}
			})
		},
		normalizeTrends(items, metricMap) {
			const groupMap = items.reduce((map, item) => {
				const code = item.metricCode
				if (!map[code]) map[code] = []
				map[code].push(item)
				return map
			}, {})
			return Object.keys(groupMap).map(code => {
				const records = groupMap[code].sort(
					(a, b) => this.dayjs(a.statDate).valueOf() - this.dayjs(b.statDate).valueOf(),
				)
				const current = records[records.length - 1] || {}
				const metric = metricMap[code] || {}
				const metricName = current.metricName || metric.label || code
				const deviation = this.toNumber(current.deviationZ ?? metric.deviation)
				const deviationCount = records.filter(item => Math.abs(this.toNumber(item.deviationZ) || 0) >= 2).length
				return {
					key: code,
					label: metricName,
					type: metric.type || this.getMetricType(code),
					status: metric.status || 'normal',
					displayLabel: current.displayLabel || metric.displayLabel,
					currentValue: this.hasValue(current.metricValue)
						? this.formatMetricNumber(current.metricValue, metricName)
						: metric.value,
					historyMean: this.hasValue(current.historyMean)
						? this.formatMetricNumber(current.historyMean, metricName)
						: metric.historyMean,
					sigma: this.hasValue(current.historyStd)
						? this.formatMetricNumber(current.historyStd, metricName)
						: metric.historySigma,
					deviation,
					deviationDays: `${deviationCount}/${records.length}`,
					data: records.map(item => ({
						date: item.statDate ? this.dayjs(item.statDate).format('MM-DD') : '--',
						value: this.toMetricNumber(item.metricValue, metricName),
					})),
				}
			})
		},
		normalizeConclusion(conclusion, deviationMetrics, metricMap) {
			const abnormalMetrics = deviationMetrics.filter(item => this.isAbnormalMetric(item))
			const suggestionMetrics = conclusion.metricSuggestions || []
			const keyFindingsSource = abnormalMetrics.length ? abnormalMetrics : suggestionMetrics
			const keyFindings = keyFindingsSource.map(item => {
				const metric = metricMap[item.metricCode] || {}
				const metricName = item.metricName || metric.label
				const deviation = this.toNumber(item.deviationZ ?? metric.deviation)
				return {
					label: metricName || item.metricCode,
					value: String(
						this.hasValue(item.metricValue)
							? this.formatMetricNumber(item.metricValue, metricName)
							: metric.value,
					),
					status:
						item.displayLabel ||
						metric.displayLabel ||
						item.abnormalCategoryName ||
						metric.abnormalCategoryName ||
						'--',
					deviation: deviation === null || deviation === undefined ? '--' : `${deviation.toFixed(2)}σ`,
				}
			})
			return {
				summary:
					conclusion.finalStatus || conclusion.portraitType
						? `当前样本判断为${conclusion.finalStatus || '--'}，画像类型为${
								conclusion.portraitType || '--'
						  }，规则偏离指标${conclusion.abnormalMetricCount ?? abnormalMetrics.length ?? 0}项。`
						: '暂无画像结论。',
				keyFindings,
				recommendations: [
					conclusion.distanceHandlingSuggestion,
					...suggestionMetrics.map(item => item.handlingSuggestion),
				].filter(Boolean),
			}
		},
		normalizeStatus(value) {
			return String(value || '').includes('异常') ? 'abnormal' : 'normal'
		},
		isAbnormalMetric(item = {}) {
			const displayLabel = String(item.displayLabel || '')
			return (
				displayLabel === '偏离' ||
				displayLabel === '异常' ||
				displayLabel.includes('偏高') ||
				displayLabel.includes('偏低')
			)
		},
		getDirection(label, deviation) {
			if (String(label || '').includes('高')) return 'up'
			if (String(label || '').includes('低')) return 'down'
			if (deviation > 0) return 'up'
			if (deviation < 0) return 'down'
			return ''
		},
		getMetricType(code = '') {
			const key = String(code).toLowerCase()
			if (key.includes('flow')) return 'flow'
			if (key.includes('temp')) return 'temperature'
			return 'pressure'
		},
		getMetricUnit(name = '') {
			return String(name).includes('占比') || String(name).includes('率') ? '%' : ''
		},
		isRateMetric(name = '') {
			return String(name).includes('占比') || String(name).includes('率')
		},
		hasValue(value) {
			return value !== null && value !== undefined && value !== ''
		},
		toNumber(value) {
			if (value === null || value === undefined || value === '') return null
			const numberValue = Number(value)
			return Number.isNaN(numberValue) ? null : numberValue
		},
		toMetricNumber(value, name) {
			const numberValue = this.toNumber(value)
			if (numberValue === null) return null
			return this.isRateMetric(name) ? numberValue * 100 : numberValue
		},
		formatMetricNumber(value, name) {
			const numberValue = this.toMetricNumber(value, name)
			if (numberValue === null) return '--'
			return Number(numberValue.toFixed(2))
		},
		formatNumber(value) {
			const numberValue = this.toNumber(value)
			if (numberValue === null) return '--'
			return Number(numberValue.toFixed(2))
		},
		getEmptyProfileData(deviceNo = this.selectedDevice || '--') {
			return {
				deviceNo,
				status: 'normal',
				analyzeTime: '--',
				statDate: '--',
				anomalyType: '--',
				clusterDistance: '--',
				pressure: { status: 'normal', items: [] },
				flow: { status: 'normal', items: [] },
				temperature: { status: 'normal', items: [] },
				deviations: [],
				trends: [],
				conclusion: {
					summary: '暂无画像结论。',
					keyFindings: [],
					recommendations: [],
				},
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.operation-profile-page {
	--page-bg: #eef4f8;
	--panel-bg: rgba(255, 255, 255, 0.96);
	--panel-border: #dbe5ee;
	--panel-shadow: 0 18px 38px rgba(15, 23, 42, 0.05);
	--title: #0f172a;
	--text: #334155;
	--muted: #64748b;
	--line: #e2e8f0;
	--primary: #2563eb;
	--primary-soft: #eff6ff;

	padding: 12px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	height: 100%;
	overflow-y: auto;
	background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 26%),
		radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 20%),
		linear-gradient(180deg, #f5f9fc 0%, var(--page-bg) 100%);
	font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
	color: var(--text);

	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-track {
		background: #f5f5f5;
		border-radius: 3px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c1c1c1;
		border-radius: 3px;
		&:hover {
			background: #a8a8a8;
		}
	}
}

.operation-profile-page > * {
	flex-shrink: 0;
}

.hero-panel {
	position: relative;
	overflow: hidden;
	padding: 24px 28px 22px;
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 255, 0.94)), var(--panel-bg);
}

.hero-panel__glow {
	position: absolute;
	width: 280px;
	height: 280px;
	border-radius: 50%;
	filter: blur(18px);
	opacity: 0.25;
	pointer-events: none;
}

.hero-panel__glow--left {
	left: -120px;
	top: -120px;
	background: rgba(37, 99, 235, 0.22);
}

.hero-panel__glow--right {
	right: -100px;
	top: -120px;
	background: rgba(14, 165, 233, 0.22);
}

.page-header {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
}

.page-header__title {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.page-title {
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	letter-spacing: 0.01em;
	color: var(--title);
}

.page-subtitle {
	margin: 0;
	font-size: 14px;
	line-height: 1.6;
	color: var(--muted);
}

.header-actions {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
}

.selected-station {
	width: 280px;
	height: 40px;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 12px;
	border: 1px solid var(--line);
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.86);
	cursor: pointer;
	transition: border-color 0.2s, box-shadow 0.2s;

	&:hover {
		border-color: var(--primary);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
	}
}

.selected-station__label {
	font-size: 12px;
	color: var(--muted);
	flex-shrink: 0;
}

.selected-station__value {
	min-width: 0;
	font-size: 14px;
	color: var(--text);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.section-block {
	flex-shrink: 0;
}

@media (max-width: 1240px) {
	.page-header {
		flex-direction: column;
		align-items: flex-start;
	}

	.header-actions {
		width: 100%;
	}

	.selected-station {
		flex: 1;
	}
}
</style>
